// Test assignment configuration shared by the Tests, Student and Class pages.
// Every assign goes through components/TestConfigPopup, which builds its state
// with buildInitialConfig and sends configToPayload to the server.

export type TimeConstraintOption = {
    type: string;
    description: string;
    // null = keep each student's own accommodation (class assignments only)
    value: number | null;
};

export const TIME_CONSTRAINT: TimeConstraintOption[] = [
    {
        type: 'Standard',
        description: 'Simulates standard allotment',
        value: 1
    },
    {
        type: '50% Extra',
        description: 'Simulates 1.5x extended time accommodations',
        value: 1.5
    },
    {
        type: '100% Extra',
        description: 'Simulates 2x extended time accommodations',
        value: 2
    },
    {
        type: 'Untimed',
        description: 'Allows for practice with unlimited time',
        value: 0
    },
];

export const STUDENT_ACCOMMODATION_OPTION: TimeConstraintOption = {
    type: "Student's accommodation",
    description: "Uses each student's own extended-time setting",
    value: null
};

export const TEST_FORMATS = [
    {
        type: 'Adaptive',
        description: 'Module 2 difficulty algorithmically set'
    },
    {
        type: 'Fixed',
        description: 'Select module 2 difficulty'
    }
];

export const ADMINISTRATION_MODES = [
    {
        value: 'adaptive',
        type: 'Adaptive',
        description: "Engine alternates Medium / Hard tier per slot based on the student's prior answers."
    },
    {
        value: 'hard_only',
        type: 'Hard only',
        description: 'Every slot served as the Hard variant. Use for students who need the maximum difficulty.'
    },
    {
        value: 'medium_only',
        type: 'Medium only',
        description: 'Every slot served as the Medium variant. Use for students who need a steady, less demanding difficulty.'
    },
];

export const SUBJECTS = {
    'Reading and Writing': 'RW',
    'Math': 'M',
    'ACT English': 'E',
    'ACT Mathematics': 'M',
    'ACT Reading': 'R',
    'ACT Science': 'S',
    'English Language Arts': 'ELA',
    'Mathematics': 'M',
    'TSIA2 ELAR': 'ELAR',
    'TSIA2 Mathematics': 'M',
};

// Test types whose assignments have per-section options. Paper tests always
// get every section, so their popup only offers timing. TSIA2 is always
// assigned whole as well: its stages are routed by the student's own CRC
// score, not chosen by the tutor.
export const CONFIGURABLE_TEST_TYPES = ['Digital SAT', 'Digital PSAT', 'Digital ACT', 'Digital SHSAT'];

// Test types with no clock. The real TSIA2 is untimed, so a timing choice
// would be a promise the player cannot keep.
export const UNTIMED_TEST_TYPES = ['TSIA2'];

export const formatDuration = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    let result = '';

    if (minutes > 0) {
        result += `${minutes} min${minutes !== 1 ? 's ' : ' '}`;
    }
    if (seconds > 0) {
        result += `${seconds} sec${seconds !== 1 ? 's ' : ' '}`;
    }
    return result;
};

export const timeConstraintLabel = (timeExtension) => (
    TIME_CONSTRAINT.find(option => option.value === parseFloat(timeExtension))?.type
);

// The test as the popup needs it. Built from any of the three list payloads.
export type ConfigTest = {
    testId: string;
    name: string;
    type: string;
    isSHSATAdaptive?: boolean;
    ttsEligible?: boolean;
    testSections: {
        subject: string;
        questionCount?: number;
        testTime?: number;
        adaptive?: boolean;
    }[];
};

// An existing assignment's settings (a Tests-page student row, a Student-page
// test row, or a class test). Absent for a new assignment.
export type ExistingAssignment = {
    testSections: { subject: string; moduleTwoDifficulty?: string | null; score?: number | null }[];
    timeExtension?: number | string | null;
    ttsEnabled?: boolean;
    administrationMode?: string | null;
    lockNavigation?: boolean | null;
};

export type ConfigSection = {
    subject: string;
    questionCount?: number;
    testTime?: number;
    assigned: boolean;
    moduleTwoDifficulty: string | null;
    score: number | null;
};

export type AssignConfig = {
    sections: ConfigSection[];
    // true = Adaptive, false = Fixed, null = not offered (ACT, SHSAT, Paper)
    adaptive: boolean | null;
    timeConstraint: number | null;
    administrationMode: string | null;
    ttsEnabled: boolean;
    allowFreeNavigation: boolean;
};

export const isConfigurable = (test: ConfigTest) => CONFIGURABLE_TEST_TYPES.includes(test.type);

export const isUntimed = (test: ConfigTest) => UNTIMED_TEST_TYPES.includes(test.type);

export const isLegacyShsat = (test: ConfigTest) => test.type === 'Digital SHSAT' && !test.isSHSATAdaptive;

export const buildInitialConfig = (
    test: ConfigTest,
    existing: ExistingAssignment | null,
    defaultTimeConstraint: number | null
): AssignConfig => {
    const existingSection = (subject: string) => existing?.testSections.find(section => section.subject === subject);
    const hasModuleTwoChoice = ['Digital SAT', 'Digital PSAT'].includes(test.type)
        && test.testSections.some(section => section.adaptive);

    const sections = test.testSections.map(section => {
        const current = existingSection(section.subject);
        return {
            subject: section.subject,
            questionCount: section.questionCount,
            testTime: section.testTime,
            // A new assignment starts with every section; an existing one
            // with the sections it has that aren't already scored.
            assigned: existing ? !!current && (current.score ?? null) === null : true,
            moduleTwoDifficulty: hasModuleTwoChoice ? (current?.moduleTwoDifficulty || 'Easy') : null,
            score: current?.score ?? null,
        };
    });

    const existingTime = existing?.timeExtension;

    return {
        sections,
        adaptive: hasModuleTwoChoice
            ? !existing?.testSections.some(section => (section.score ?? null) === null && section.moduleTwoDifficulty)
            : null,
        timeConstraint: existingTime !== undefined && existingTime !== null && existingTime !== ''
            ? parseFloat(String(existingTime))
            : defaultTimeConstraint,
        administrationMode: test.isSHSATAdaptive
            ? (existing?.administrationMode && existing.administrationMode !== 'legacy_fixed' ? existing.administrationMode : 'adaptive')
            : null,
        ttsEnabled: !!existing?.ttsEnabled,
        // Legacy SHSAT: on only when this assignment was explicitly unlocked.
        // NULL (inherit the global lock) and true both mean locked.
        allowFreeNavigation: existing?.lockNavigation === false,
    };
};

export const configToPayload = (test: ConfigTest, config: AssignConfig) => {
    const payload: any = {
        // An untimed test sends no timing choice; the server leaves the
        // student's own setting alone when the field is null.
        timeConstraint: isUntimed(test) ? null : config.timeConstraint,
        ttsEnabled: !!test.ttsEligible && config.ttsEnabled,
    };

    if (isConfigurable(test)) {
        payload.subjects = config.sections.filter(section => section.assigned).map(section => ({
            subject: section.subject,
            moduleTwoDifficulty: config.adaptive === false ? section.moduleTwoDifficulty : null,
        }));
    }
    if (config.administrationMode !== null) {
        payload.administrationMode = config.administrationMode;
    }
    if (isLegacyShsat(test)) {
        payload.lockNavigation = !config.allowFreeNavigation;
    }

    return payload;
};
